import React, { useEffect, useRef, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';
import SkudPersonEditModal from '@/components/SkudPersonEditModal';

/**
 * Пикер человека в СКУД для привязки к карточке ученика/сотрудника/родителя.
 *
 * Источник — локальный кэш `skud_persons`. Список фильтруется по
 * подразделению, замапленному в /settings/skud:
 *   role='student'  → department_students_skud_id
 *   role='employee' → department_employees_skud_id
 *   role='parent'   → department_parents_skud_id
 *
 * value:    { skudId, lastName, firstName, middleName?, photoThumb?, isDismissed?, pendingSync? } | null
 * onChange: (newValue | null) — null значит «отвязать»
 *
 * Если у пользователя есть право `skud.persons/write`, снизу пикера
 * появляется кнопка «+ Создать нового» — открывает модалку CreateSkudPerson.
 * После создания запись сразу привязывается к карточке; отрицательный skud_id
 * жёлтой плашкой сигналит, что человек ещё не ушёл в Orion.
 */
export default function SkudPersonPicker({ value, onChange, role, disabled }) {
  const can = useCan();
  const canCreate = can('skud.persons', 'write');

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const ref = useRef(null);

  // Закрытие по клику вне.
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  // Дебаунс поиска.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(async () => {
      setLoading(true); setError(null);
      try {
        const qs = new URLSearchParams();
        if (role)   qs.set('role', role);
        if (search) qs.set('q', search);
        qs.set('exclude_dismissed', '1');
        qs.set('limit', '50');
        const data = await api.get('/api/skud/persons?' + qs.toString());
        setResults(data.rows || []);
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [search, open, role]);

  const fullName = (p) => [p.lastName, p.firstName, p.middleName].filter(Boolean).join(' ');
  const display  = value ? (fullName(value) || `#${value.skudId}`) : '';
  const valuePending = value && (value.pendingSync || Number(value.skudId) < 0);

  const onCreated = (person) => {
    // Модалка вернула сохранённую запись — привязываем сразу.
    onChange(person);
    setOpen(false);
  };

  return (
    <div className="ad-picker" ref={ref}>
      <div className="ad-picker__row">
        <button
          type="button"
          className="ad-picker__current"
          onClick={() => !disabled && setOpen((o) => !o)}
          disabled={disabled}
        >
          {value ? (
            <span className="ad-picker__value">
              {value.photoThumb && (
                <img
                  src={value.photoThumb}
                  alt=""
                  style={{
                    width: 22, height: 22, borderRadius: '50%',
                    objectFit: 'cover', marginRight: 8,
                  }}
                />
              )}
              <span className="ad-picker__name"
                    style={value.isDismissed ? { color: 'var(--fg-muted)', textDecoration: 'line-through' } : undefined}>
                {display}
              </span>
              <span className="mono ad-picker__upn">
                {valuePending ? 'ожидает синхронизации' : `СКУД #${value.skudId}`}
              </span>
            </span>
          ) : (
            <span className="ad-picker__empty">Выберите человека из СКУД…</span>
          )}
          <Icon name="chevronDown" size={10} />
        </button>
        {value && !disabled && (
          <button
            type="button"
            className="ad-picker__clear"
            onClick={() => onChange(null)}
            title="Отвязать"
          >
            <Icon name="x" size={12} />
          </button>
        )}
      </div>

      {open && (
        <div className="ad-picker__menu">
          <div style={{ padding: 6 }}>
            <input
              className="ad-picker__search"
              placeholder="Поиск: ФИО"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
              style={{ width: '100%' }}
            />
          </div>
          {error && <div className="ad-picker__error">{error}</div>}
          <div className="ad-picker__list">
            {loading && <div className="ad-picker__hint">Поиск в кэше СКУД…</div>}
            {!loading && !error && results.map((r) => (
              <button
                key={r.skudId}
                type="button"
                className="ad-picker__item"
                onClick={() => { onChange(r); setOpen(false); }}
              >
                <span className="ad-picker__item-name" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {r.photoThumb && (
                    <img src={r.photoThumb} alt=""
                         style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'cover' }} />
                  )}
                  <span>{fullName(r)}</span>
                </span>
                <span className="ad-picker__item-meta">
                  {r.department && <span style={{ marginRight: 8 }}>{r.department}</span>}
                  <span className="mono">#{r.skudId}</span>
                </span>
              </button>
            ))}
            {!loading && !error && results.length === 0 && (
              <div className="ad-picker__hint">
                {search
                  ? 'Ничего не найдено в выбранном подразделении.'
                  : 'Начните вводить ФИО'}
              </div>
            )}
          </div>
          {canCreate && (
            <div className="ad-picker__footer" style={{ borderTop: '1px solid var(--border)', padding: 6 }}>
              <button
                type="button"
                className="btn btn--sm"
                style={{ width: '100%' }}
                onClick={() => { setCreateOpen(true); setOpen(false); }}
              >
                <Icon name="plus" size={12} /> Создать нового человека в СКУД
              </button>
            </div>
          )}
        </div>
      )}

      {createOpen && (
        <SkudPersonEditModal
          mode="create"
          role={role}
          onClose={() => setCreateOpen(false)}
          onSaved={onCreated}
        />
      )}
    </div>
  );
}
